import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// Helper to convert VAPID base64 string to Uint8Array required by pushManager
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if service worker and push messaging are supported
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      setPermission(Notification.permission);
      checkSubscriptionStatus();
    }
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
          setIsSubscribed(false);
          return;
      }
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (err) {
      console.error('Error checking push subscription:', err);
    }
  };

  const subscribe = async (tournamentId, teamId = null) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Request permission
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        throw new Error('Toestemming voor notificaties niet gegeven.');
      }

      // 2. Get Service Worker registration
      const registration = await navigator.serviceWorker.ready;

      // 3. Get VAPID Key from environment variables (must be added in .env)
      const publicVapidKey = import.meta.env.VITE_PUBLIC_VAPID_KEY;
      if (!publicVapidKey) {
        throw new Error('Push Notificaties zijn nog niet geconfigureerd op de server (VAPID key ontbreekt).');
      }

      // 4. Subscribe to PushManager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicVapidKey)
      });

      // 5. Extract keys
      const p256dh = btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh'))));
      const auth = btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))));

      // 6. Save to Supabase
      const { error: dbError } = await supabase
        .from('push_subscriptions')
        .insert({
          tournament_id: tournamentId,
          team_id: teamId,
          endpoint: subscription.endpoint,
          auth_keys: {
            p256dh,
            auth
          }
        });

      if (dbError) throw dbError;

      setIsSubscribed(true);
      return true;

    } catch (err) {
      console.error('Push Subscription Error:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe
  };
}
